import {
  ENDPOINTS,
  NominateBaseContractRequestSchema,
  type ListBaseContractsResponse,
  type NominateBaseContractRequest,
  type NominateBaseContractResponse,
} from '@clm/shared-types';

import { apiGet, apiPost } from './client';

/**
 * The base MSA — CLM-111 (FR-37, DR-11).
 *
 * `listBaseContracts` reads the current nomination and every earlier version
 * per enabled type (`clause:review_match` — Admin and Legal, no audit row).
 * `nominateBaseContract` nominates a contract as the base, or replaces the
 * current one (`admin:configure` — Admin only); the same call does both, and
 * `expected_current_nomination_id` — the current nomination the page showed,
 * or `null` — is what makes a stale page a `409 base_changed` rather than a
 * silent overwrite of another admin's decision.
 *
 * The request is validated `.strict()` before anything is sent; every
 * response is parsed, not asserted, so an off-contract body — a current row
 * that is superseded, a replacement with one audit id — is a `ZodError`, never
 * a page showing a base that is not the base.
 *
 * NFR-06: no `scope` key is ever sent — scope is the API's, from the token;
 * `scope_kind` on the list is how the page learns it.
 */

const LIST = ENDPOINTS['list-base-contracts'];
const NOMINATE = ENDPOINTS['nominate-base-contract'];

export async function listBaseContracts(signal?: AbortSignal): Promise<ListBaseContractsResponse> {
  const raw = await apiGet<unknown>(LIST.path, signal === undefined ? {} : { signal });
  return LIST.response.parse(raw);
}

export async function nominateBaseContract(
  request: NominateBaseContractRequest,
  signal?: AbortSignal,
): Promise<NominateBaseContractResponse> {
  const body = NominateBaseContractRequestSchema.parse(request);
  const raw = await apiPost<unknown>(NOMINATE.path, body, signal === undefined ? {} : { signal });
  return NOMINATE.response.parse(raw);
}
